import React, { useState } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useCompany } from '@/contexts/CompanyContext';
import { db } from '@/lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Switch } from '@/components/ui/switch';

const AVAILABLE_MODULES = [
    { id: 'crm', name: 'Oblique CRM', description: 'Pipeline, contacts, and sales automation.', icon: 'group' },
    { id: 'quality', name: 'Quality Assurance', description: 'Revenue protection, deal audits, and compliance.', icon: 'verified' },
    { id: 'lms', name: 'Aspect LMS', description: 'Learning management and team training.', icon: 'school' },
    { id: 'analytics', name: 'Momentum Analytics', description: 'Deep insights and reporting dashboards.', icon: 'bar_chart' },
    { id: 'orbit', name: 'Orbit Automation', description: 'Cross-platform workflows and triggers.', icon: 'webhook' },
    { id: 'hr', name: 'HR & People', description: 'Employee management and recruitment.', icon: 'groups' },
    { id: 'finance', name: 'Capital Finance', description: 'Invoicing, billing, and accounting.', icon: 'payments' },
    { id: 'yoda', name: 'Yoda AI Engine', description: 'Generative AI and automated analysis.', icon: 'yoda' },
];

export default function ModuleManagement() {
    const { currentCompany } = useCompany();
    const [updating, setUpdating] = useState(null);

    const enabledModules = currentCompany?.enabledModules || ['crm', 'quality', 'lms', 'analytics', 'orbit'];

    const toggleModule = async (moduleId) => {
        if (!currentCompany?.id) return;
        
        setUpdating(moduleId);
        try {
            const isEnabled = enabledModules.includes(moduleId);
            let newModules;
            if (isEnabled) {
                newModules = enabledModules.filter(id => id !== moduleId);
            } else {
                newModules = [...enabledModules, moduleId];
            }

            await updateDoc(doc(db, 'app_companies', currentCompany.id), {
                enabledModules: newModules
            });
        } catch (error) {
            console.error('Failed to toggle module:', error);
        } finally {
            setUpdating(null);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
                <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}>Module Management</h2>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                    Control which applications are active for your workspace. Disabled modules will be hidden from all users.
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {AVAILABLE_MODULES.map(mod => {
                    const isEnabled = enabledModules.includes(mod.id);
                    const isUpdating = updating === mod.id;

                    return (
                        <div 
                            key={mod.id} 
                            style={{ 
                                display: 'flex', flexDirection: 'column',
                                padding: '24px', borderRadius: '16px', 
                                border: isEnabled ? '1px solid var(--accent)' : '1px solid var(--border)', 
                                background: 'var(--bg-surface)',
                                boxShadow: isEnabled ? '0 8px 24px -8px var(--accent)' : 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <div style={{ 
                                    width: '44px', height: '44px', borderRadius: '12px', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'all 200ms ease',
                                    background: isEnabled ? 'var(--accent)' : 'var(--bg-muted)',
                                    color: isEnabled ? 'var(--bg-surface)' : 'var(--text-tertiary)'
                                }}>
                                    <ObliqueIcon name={mod.icon} size={22} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isUpdating && (
                                        <div className="animate-spin" style={{ color: 'var(--text-tertiary)', display: 'flex' }}>
                                            <ObliqueIcon name="sync" size={16} />
                                        </div>
                                    )}
                                    <Switch 
                                        checked={isEnabled} 
                                        onCheckedChange={() => toggleModule(mod.id)}
                                        disabled={isUpdating}
                                    />
                                </div>
                            </div>
                            
                            <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px', color: 'var(--text-primary)' }}>{mod.name}</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', lineHeight: '1.4' }}>{mod.description}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
